<template>
  <div class="bg-white shadow-xl rounded-lg p-8 max-w-md w-full">
    <h2 class="text-2xl font-bold text-foreground mb-6 text-center">Register</h2>
    
    <form @submit.prevent="handleRegister">
      <div class="mb-4">
        <label for="fullname" class="block text-foreground text-sm font-medium mb-2">Full Name</label>
        <input
          type="text"
          id="fullname"
          v-model="fullname"
          class="w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          placeholder="Enter your full name"
          required
        >
      </div>
      
      <div class="mb-4">
        <label for="email" class="block text-foreground text-sm font-medium mb-2">Email</label>
        <input
          type="email"
          id="email"
          v-model="email"
          class="w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          placeholder="Enter your email"
          required
        >
      </div>
      
      <div class="mb-4">
        <label for="username" class="block text-foreground text-sm font-medium mb-2">Username</label>
        <input
          type="text"
          id="username"
          v-model="username"
          class="w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          placeholder="Choose a username"
          required
        >
      </div>
      
      <div class="mb-4">
        <label for="password" class="block text-foreground text-sm font-medium mb-2">Password</label>
        <input
          type="password"
          id="password"
          v-model="password"
          class="w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          placeholder="Choose a password"
          required
        >
      </div>
      
      <div class="mb-6">
        <label for="confirmPassword" class="block text-foreground text-sm font-medium mb-2">Confirm Password</label>
        <input
          type="password"
          id="confirmPassword"
          v-model="confirmPassword"
          class="w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          placeholder="Confirm your password"
          required
        >
      </div>
      
      <div v-if="validationError || error" class="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
        {{ validationError || error.message }}
      </div>
      
      <button
        type="submit"
        class="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out mb-4"
        :disabled="loading"
      >
        <span v-if="loading">Registering...</span>
        <span v-else>Register</span>
      </button>
      
      <div class="text-center">
        <button
          type="button"
          @click="switchToLogin"
          class="text-primary hover:text-primary/80 text-sm font-medium"
        >
          Already have an account? Login
        </button>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useAuth } from '@/composables/useAuth';
import type { RegistrationData } from '@/types/user';
import { logger, LogCategory } from '@/services/logger';

export default defineComponent({
  name: 'RegisterForm',
  emits: ['register-success', 'switch-to-login'],
  setup(props, { emit }) {
    const { register, loading, error } = useAuth();
    
    const fullname = ref('');
    const email = ref('');
    const username = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const validationError = ref<string | null>(null);
    
    async function handleRegister() {
      if (loading.value) return;
      
      // Clear previous errors
      validationError.value = null;
      
      // Validate passwords match
      if (password.value !== confirmPassword.value) {
        validationError.value = 'Passwords do not match';
        return;
      }
      
      try {
        const registrationData: RegistrationData = {
          fullname: fullname.value,
          email: email.value,
          username: username.value,
          password: password.value,
          confirmPassword: confirmPassword.value
        };
        
        await register(registrationData);
        
        emit('register-success', {
          fullname: fullname.value,
          email: email.value,
          username: username.value
        });
        
        // Reset form
        fullname.value = '';
        email.value = '';
        username.value = '';
        password.value = '';
        confirmPassword.value = '';
      } catch (err) {
        // Error is already handled in the useAuth composable
        logger.error('Registration failed', LogCategory.AUTH, { error: err });
      }
    }
    
    function switchToLogin() {
      emit('switch-to-login');
    }
    
    return {
      fullname,
      email,
      username,
      password,
      confirmPassword,
      validationError,
      loading,
      error,
      handleRegister,
      switchToLogin
    };
  }
});
</script>